import React, { useState, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Search, Download, Copy, Check, Loader2, Shuffle, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import { TCL_QUOTES } from '../../data/quotes';

const SITE_URL = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace('/api', '').replace(':8001', ':3000')
  : 'https://thirdcultureleadership.co.uk';

/* ── Dimensions ── */
const DIMENSIONS = {
  '4:5':  { id: '4:5',  label: '4:5',  sublabel: 'Instagram Feed',  w: 540, h: 675, downloadScale: 2 },
  '1:1':  { id: '1:1',  label: '1:1',  sublabel: 'Square Post',      w: 540, h: 540, downloadScale: 2 },
  '16:9': { id: '16:9', label: '16:9', sublabel: 'Landscape',        w: 720, h: 405, downloadScale: 2 },
  '9:16': { id: '9:16', label: '9:16', sublabel: 'Story / Reel',     w: 405, h: 720, downloadScale: 2 },
};

/* ── Themes ── */
const THEMES = {
  dark: {
    id: 'dark', label: 'Navy',
    bg: '#071B3A',
    bgAccent: 'rgba(201,147,22,0.06)',
    accent: '#C99316',
    text: '#FAF8F3',
    subText: 'rgba(250,248,243,0.42)',
    bar: '#C99316',
    divider: 'rgba(201,147,22,0.2)',
    footerText: 'rgba(250,248,243,0.22)',
    quoteMarkColor: 'rgba(201,147,22,0.07)',
    cornerColor: 'rgba(201,147,22,0.3)',
    swatch: '#071B3A',
  },
  ivory: {
    id: 'ivory', label: 'Ivory',
    bg: '#F8F5EE',
    bgAccent: 'rgba(7,27,58,0.04)',
    accent: '#B8860B',
    text: '#071B3A',
    subText: 'rgba(7,27,58,0.42)',
    bar: '#B8860B',
    divider: 'rgba(7,27,58,0.12)',
    footerText: 'rgba(7,27,58,0.28)',
    quoteMarkColor: 'rgba(7,27,58,0.06)',
    cornerColor: 'rgba(7,27,58,0.15)',
    swatch: '#F8F5EE',
  },
  gold: {
    id: 'gold', label: 'Gold',
    bg: '#C99316',
    bgAccent: 'rgba(7,27,58,0.08)',
    accent: '#071B3A',
    text: '#071B3A',
    subText: 'rgba(7,27,58,0.58)',
    bar: '#071B3A',
    divider: 'rgba(7,27,58,0.2)',
    footerText: 'rgba(7,27,58,0.38)',
    quoteMarkColor: 'rgba(7,27,58,0.07)',
    cornerColor: 'rgba(7,27,58,0.25)',
    swatch: '#C99316',
  },
  midnight: {
    id: 'midnight', label: 'Midnight',
    bg: '#040C18',
    bgAccent: 'rgba(201,147,22,0.04)',
    accent: '#C99316',
    text: '#FAF8F3',
    subText: 'rgba(250,248,243,0.38)',
    bar: '#C99316',
    divider: 'rgba(201,147,22,0.15)',
    footerText: 'rgba(250,248,243,0.18)',
    quoteMarkColor: 'rgba(201,147,22,0.05)',
    cornerColor: 'rgba(201,147,22,0.2)',
    swatch: '#040C18',
  },
};

const SITE_SECTIONS = [
  { label: 'Home', path: '/' },
  { label: 'Articles', path: '/articles' },
  { label: 'Cultural Fluency Matrix', path: '/cfq-matrix' },
  { label: 'CFQ Assessment', path: '/cfq-assessment' },
  { label: 'The Four Pillars', path: '/pillars' },
  { label: 'About', path: '/about' },
  { label: 'Speaking', path: '/speaking' },
  { label: 'Courses', path: '/courses' },
];

/* ── Font size for quote text — aggressive scaling to prevent crowding ── */
const getQuoteFontSize = (quote, cardW, cardH) => {
  const len = quote.length;
  const isLandscape = cardW > cardH;
  const isStory = cardH > cardW * 1.5;
  if (isLandscape) {
    if (len < 60)  return 22;
    if (len < 100) return 18;
    if (len < 150) return 15;
    return 13;
  }
  if (isStory) {
    if (len < 80)  return 28;
    if (len < 140) return 24;
    if (len < 200) return 20;
    if (len < 280) return 17;
    return 14;
  }
  // Square / portrait
  if (len < 80)  return 26;
  if (len < 130) return 22;
  if (len < 180) return 19;
  if (len < 240) return 16;
  if (len < 320) return 14;
  return 12;
};

/* ────────────────────────────────────────────────
   QuoteCard — the visual captured by html2canvas
   ──────────────────────────────────────────────── */
const QuoteCard = React.forwardRef(({ quote, theme, attribution, siteLink, cardW, cardH }, ref) => {
  const t = THEMES[theme] || THEMES.dark;
  const isLandscape = cardW > cardH;
  const isStory = cardH > cardW * 1.5;
  const barW = 5;

  const padSide = isLandscape ? 40 : 52;
  const padTop  = isLandscape ? 32 : 44;
  const padBot  = isLandscape ? 28 : 40;
  const qmSize  = isLandscape ? 130 : isStory ? 220 : 190;
  const fontSize = getQuoteFontSize(quote, cardW, cardH);

  return (
    <div
      ref={ref}
      style={{
        width: `${cardW}px`,
        height: `${cardH}px`,
        background: t.bg,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Georgia, "Times New Roman", serif',
        display: 'flex',
        flexDirection: 'column',
        padding: `${padTop}px ${padSide}px ${padBot}px ${padSide + barW + 14}px`,
        boxSizing: 'border-box',
      }}
    >
      {/* Left accent bar — gradient */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: `${barW}px`, height: '100%',
        background: `linear-gradient(to bottom, ${t.accent} 0%, ${t.accent}bb 60%, ${t.accent}33 100%)`,
      }} />

      {/* Top-right corner notch */}
      <div style={{
        position: 'absolute', top: 0, right: 0,
        width: '64px', height: '64px',
        borderBottom: `1px solid ${t.cornerColor}`,
        borderLeft: `1px solid ${t.cornerColor}`,
        borderBottomLeftRadius: '20px',
      }} />

      {/* Bottom-right glow circle */}
      <div style={{
        position: 'absolute',
        bottom: isLandscape ? '-50px' : '-70px',
        right: isLandscape ? '-50px' : '-70px',
        width: isLandscape ? '180px' : '240px',
        height: isLandscape ? '180px' : '240px',
        borderRadius: '50%',
        background: t.bgAccent,
        pointerEvents: 'none',
      }} />

      {/* Large decorative opening quotation mark */}
      <div style={{
        position: 'absolute',
        top: isLandscape ? '8px' : '16px',
        left: `${barW + 38}px`,
        fontSize: `${qmSize}px`,
        lineHeight: 1,
        color: t.quoteMarkColor,
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
        fontWeight: 700,
        pointerEvents: 'none',
        userSelect: 'none',
      }}>
        &#x201C;
      </div>

      {/* Header label */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        marginBottom: isLandscape ? '18px' : '26px',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '9px',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: t.accent,
        fontWeight: 700,
        flexShrink: 0,
      }}>
        <div style={{ width: '16px', height: '1px', background: t.accent, opacity: 0.5 }} />
        THIRD CULTURE LEADERSHIP
      </div>

      {/* Quote text — vertically centred in remaining space */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <blockquote style={{
          margin: 0,
          padding: 0,
          fontSize: `${fontSize}px`,
          lineHeight: 1.6,
          color: t.text,
          fontStyle: 'italic',
          fontWeight: 400,
          letterSpacing: '-0.01em',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
        }}>
          &#x201C;{quote}&#x201D;
        </blockquote>
      </div>

      {/* Attribution */}
      <div style={{ marginTop: isLandscape ? '18px' : '26px', flexShrink: 0 }}>
        <div style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '11px',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: t.accent,
          fontWeight: 700,
        }}>
          &#x2014; {attribution || 'Promise Monday'}
        </div>
        <div style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '9px',
          letterSpacing: '0.13em',
          textTransform: 'uppercase',
          color: t.subText,
          marginTop: '5px',
        }}>
          Paediatrician &middot; Author &middot; Founder
        </div>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: isLandscape ? '14px' : '20px',
        paddingTop: '13px',
        borderTop: `1px solid ${t.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: 'Arial, Helvetica, sans-serif',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '8px', letterSpacing: '0.22em', textTransform: 'uppercase', color: t.footerText }}>
          Cultural Fluency in Leadership
        </span>
        <span style={{ fontSize: '9px', color: t.footerText, opacity: 0.8 }}>
          {siteLink || 'thirdcultureleadership.co.uk'}
        </span>
      </div>
    </div>
  );
});
QuoteCard.displayName = 'QuoteCard';

/* ────────────────────────────────────────────────
   Main Component
   ──────────────────────────────────────────────── */
const AdminQuoteCards = () => {
  const [search, setSearch]           = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [customText, setCustomText]   = useState('');
  const [useCustom, setUseCustom]     = useState(false);
  const [theme, setTheme]             = useState('dark');
  const [dimension, setDimension]     = useState('1:1');
  const [section, setSection]         = useState(SITE_SECTIONS[0]);
  const [downloading, setDownloading] = useState('');
  const [copied, setCopied]           = useState('');
  const cardRef = useRef(null);

  const filtered = TCL_QUOTES.filter(q =>
    !search || q.toLowerCase().includes(search.toLowerCase())
  );

  const activeQuote = useCustom
    ? (customText.trim() || 'Enter your quote above…')
    : (filtered[selectedIdx] || filtered[0] || TCL_QUOTES[0]);

  const siteLink = `thirdcultureleadership.co.uk${section.path === '/' ? '' : section.path}`;
  const fullUrl  = `${SITE_URL}${section.path}`;

  const handleShuffle = () => {
    setSelectedIdx(Math.floor(Math.random() * filtered.length));
    setUseCustom(false);
  };

  /* Download PNG at a specific dimension */
  const handleDownload = useCallback(async (dimId) => {
    if (!cardRef.current) return;
    const dim = DIMENSIONS[dimId];
    setDownloading(dimId);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        width: dim.w,
        height: dim.h,
      });
      const slug = activeQuote.slice(0, 28).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
      const link = document.createElement('a');
      link.download = `tcl-quote-${dimId.replace(':', 'x')}-${slug}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloading('');
  }, [activeQuote]);

  const shareCaption = `"${activeQuote}"\n\n— Promise Monday\nFounder, Third Culture Leadership\n\n${fullUrl}\n\n#ThirdCultureLeadership #CulturalFluency #Leadership`;

  const handleCopy = async (key, text) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleShare = (platform) => {
    const enc = encodeURIComponent;
    const urls = {
      linkedin: `https://www.linkedin.com/feed/?shareActive=true&text=${enc(`"${activeQuote}"\n\n— Promise Monday, Founder, Third Culture Leadership\n\n${fullUrl}\n\n#ThirdCultureLeadership #CulturalFluency`)}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc(`"${activeQuote}" — Promise Monday`)}&url=${enc(fullUrl)}&hashtags=ThirdCultureLeadership,CulturalFluency,Leadership`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(fullUrl)}`,
    };
    if (urls[platform]) window.open(urls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  /* Preview scale — fit inside a ~380px display width */
  const dim = DIMENSIONS[dimension];
  const DISPLAY_W = 380;
  const previewScale  = DISPLAY_W / dim.w;
  const previewDispH  = Math.round(dim.h * previewScale);

  return (
    <div data-testid="admin-quote-cards">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Quote Cards</h1>
          <p className="text-cream/30 text-sm mt-1">Branded social cards in 4 formats — Navy, Ivory, Gold, Midnight</p>
        </div>
        <button
          onClick={handleShuffle}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gold/20 rounded-lg text-gold/70 hover:text-gold hover:border-gold/40 transition-all text-sm"
          data-testid="quote-shuffle-btn"
        >
          <Shuffle size={14} />
          Shuffle
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">

        {/* ── Left panel ── */}
        <div className="xl:col-span-2 space-y-4">

          {/* Theme */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-3">Colour Theme</p>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {Object.values(THEMES).map(t => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t.id)}
                  data-testid={`theme-${t.id}`}
                  className={`py-2.5 rounded-lg text-xs font-medium transition-all border ${
                    theme === t.id ? 'border-gold/40 text-gold' : 'border-cream/[0.08] text-cream/40 hover:border-cream/20'
                  }`}
                  style={theme === t.id ? { background: 'rgba(201,147,22,0.08)' } : {}}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {/* Colour swatches */}
            <div className="flex gap-2">
              {Object.values(THEMES).map(t => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t.id)}
                  title={t.label}
                  className="flex-1 rounded-md h-2 transition-all"
                  style={{
                    background: t.swatch,
                    border: theme === t.id ? '2px solid #C99316' : '2px solid transparent',
                    outline: t.id === 'ivory' ? '1px solid rgba(0,0,0,0.08)' : 'none',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Format / Dimensions */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-3">Format</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(DIMENSIONS).map(d => (
                <button
                  key={d.id}
                  onClick={() => setDimension(d.id)}
                  data-testid={`dim-${d.id.replace(':', 'x')}`}
                  className={`px-3 py-3 rounded-xl text-left transition-all border ${
                    dimension === d.id
                      ? 'border-gold/35 bg-gold/[0.06]'
                      : 'border-cream/[0.07] hover:border-cream/15'
                  }`}
                >
                  <p className={`text-sm font-semibold font-mono ${dimension === d.id ? 'text-gold' : 'text-cream/50'}`}>
                    {d.label}
                  </p>
                  <p className="text-[10px] text-cream/25 mt-0.5">{d.sublabel}</p>
                  <p className="text-[9px] text-cream/15 mt-0.5">{d.w * d.downloadScale}×{d.h * d.downloadScale}px</p>
                </button>
              ))}
            </div>
          </div>

          {/* Link to section */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-3">Link to Section</p>
            <select
              value={section.path}
              onChange={e => setSection(SITE_SECTIONS.find(s => s.path === e.target.value))}
              className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold/30"
              data-testid="section-select"
            >
              {SITE_SECTIONS.map(s => (
                <option key={s.path} value={s.path}>{s.label} — {s.path}</option>
              ))}
            </select>
          </div>

          {/* Quote source */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider">Quote</p>
              <label className="flex items-center gap-2 cursor-pointer text-xs text-cream/40">
                <input
                  type="checkbox"
                  checked={useCustom}
                  onChange={e => setUseCustom(e.target.checked)}
                  className="rounded"
                  data-testid="use-custom-toggle"
                />
                Custom text
              </label>
            </div>

            {useCustom ? (
              <textarea
                value={customText}
                onChange={e => setCustomText(e.target.value)}
                placeholder="Type your own quote text…"
                rows={4}
                className="w-full bg-navy-deep border border-cream/10 rounded-lg px-3 py-2.5 text-cream text-sm resize-none focus:outline-none focus:border-gold/30 placeholder:text-cream/20"
                data-testid="custom-quote-input"
              />
            ) : (
              <>
                <div className="relative mb-3">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/20" />
                  <input
                    value={search}
                    onChange={e => { setSearch(e.target.value); setSelectedIdx(0); }}
                    placeholder={`Search ${TCL_QUOTES.length} quotes…`}
                    className="w-full pl-8 pr-3 py-2 bg-navy-deep border border-cream/10 rounded-lg text-cream text-xs focus:outline-none focus:border-gold/30 placeholder:text-cream/20"
                    data-testid="quote-search"
                  />
                </div>
                <div className="overflow-y-auto rounded-lg border border-cream/[0.05]" style={{ maxHeight: '240px' }}>
                  {filtered.slice(0, 80).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedIdx(i)}
                      data-testid={`quote-item-${i}`}
                      className={`w-full text-left px-3 py-2.5 text-xs leading-relaxed border-b border-cream/[0.04] last:border-0 transition-colors ${
                        selectedIdx === i && !useCustom
                          ? 'bg-gold/10 text-gold/80'
                          : 'text-cream/40 hover:text-cream/70 hover:bg-cream/[0.03]'
                      }`}
                    >
                      {q.length > 100 ? q.slice(0, 100) + '…' : q}
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <p className="px-3 py-4 text-cream/20 text-xs text-center">No quotes match your search.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="xl:col-span-3 space-y-5">

          {/* Card preview */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider">
                Preview — {dim.label} {dim.sublabel}
              </p>
              <p className="text-cream/15 text-[10px]">
                {dim.w * dim.downloadScale} × {dim.h * dim.downloadScale} px output
              </p>
            </div>
            <div className="flex justify-center overflow-hidden">
              <div style={{
                transform: `scale(${previewScale})`,
                transformOrigin: 'top center',
                height: `${previewDispH}px`,
                width: `${DISPLAY_W}px`,
                position: 'relative',
                flexShrink: 0,
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0 }}>
                  <QuoteCard
                    ref={cardRef}
                    quote={activeQuote}
                    theme={theme}
                    attribution="Promise Monday"
                    siteLink={siteLink}
                    cardW={dim.w}
                    cardH={dim.h}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Download options */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-4">Download</p>

            {/* Primary — selected format */}
            <button
              onClick={() => handleDownload(dimension)}
              disabled={!!downloading}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl font-semibold text-sm mb-3 transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #C99316 0%, #A07810 100%)', color: '#071B3A' }}
              data-testid="download-card-btn"
            >
              {downloading === dimension
                ? <Loader2 size={16} className="animate-spin" />
                : <Download size={16} />}
              {downloading === dimension
                ? 'Generating PNG…'
                : `Download ${dim.label} — ${dim.sublabel}`}
            </button>

            {/* All formats grid */}
            <div className="grid grid-cols-4 gap-2">
              {Object.values(DIMENSIONS).map(d => (
                <button
                  key={d.id}
                  onClick={() => handleDownload(d.id)}
                  disabled={!!downloading}
                  data-testid={`download-dim-${d.id.replace(':', 'x')}`}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl text-center transition-all disabled:opacity-50 border ${
                    d.id === dimension
                      ? 'border-gold/30 bg-gold/[0.06]'
                      : 'border-cream/[0.07] hover:border-cream/15'
                  }`}
                >
                  {downloading === d.id
                    ? <Loader2 size={12} className="animate-spin text-gold" />
                    : <Download size={12} className={d.id === dimension ? 'text-gold' : 'text-cream/30'} />}
                  <span className={`text-[11px] font-mono font-semibold leading-none ${d.id === dimension ? 'text-gold' : 'text-cream/40'}`}>
                    {d.label}
                  </span>
                  <span className="text-[9px] text-cream/20 leading-tight">{d.sublabel}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Share buttons */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-4">Share to Platform</p>
            <div className="grid grid-cols-2 gap-3">

              <button
                onClick={() => { handleCopy('li', shareCaption); handleShare('linkedin'); }}
                className="flex items-center gap-3 px-4 py-3 border rounded-xl transition-all text-sm border-[#0A66C2]/20 text-[#0A66C2]/70 hover:text-[#0A66C2] hover:border-[#0A66C2]/40 hover:bg-[#0A66C2]/[0.06]"
                data-testid="share-linkedin-btn"
              >
                <Linkedin size={16} />
                <div className="text-left">
                  <p className="text-xs font-medium">LinkedIn</p>
                  <p className="text-[10px] opacity-60">Opens composer</p>
                </div>
              </button>

              <button
                onClick={() => handleShare('twitter')}
                className="flex items-center gap-3 px-4 py-3 border border-cream/10 rounded-xl text-cream/50 hover:text-cream hover:border-cream/25 hover:bg-cream/[0.04] transition-all text-sm"
                data-testid="share-twitter-btn"
              >
                <Twitter size={16} />
                <div className="text-left">
                  <p className="text-xs font-medium">X (Twitter)</p>
                  <p className="text-[10px] opacity-60">Pre-filled intent</p>
                </div>
              </button>

              <button
                onClick={() => handleShare('facebook')}
                className="flex items-center gap-3 px-4 py-3 border rounded-xl transition-all text-sm border-[#1877F2]/20 text-[#1877F2]/60 hover:text-[#1877F2] hover:border-[#1877F2]/35 hover:bg-[#1877F2]/[0.05]"
                data-testid="share-facebook-btn"
              >
                <Facebook size={16} />
                <div className="text-left">
                  <p className="text-xs font-medium">Facebook</p>
                  <p className="text-[10px] opacity-60">Opens share dialog</p>
                </div>
              </button>

              <button
                onClick={() => handleCopy('caption', shareCaption)}
                className="flex items-center gap-3 px-4 py-3 border border-cream/10 rounded-xl text-cream/50 hover:text-gold hover:border-gold/25 transition-all text-sm"
                data-testid="copy-caption-btn"
              >
                {copied === 'caption' ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
                <div className="text-left">
                  <p className="text-xs font-medium">{copied === 'caption' ? 'Copied!' : 'Copy Caption'}</p>
                  <p className="text-[10px] opacity-60">Instagram / TikTok</p>
                </div>
              </button>
            </div>

            <div className="mt-3 bg-cream/[0.025] border border-cream/[0.05] rounded-xl p-4 flex items-start gap-3">
              <Instagram size={14} className="text-cream/30 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-cream/25 leading-relaxed">
                <strong className="text-cream/45">Instagram &amp; TikTok:</strong> Download the card, upload to the platform, then paste the copied caption.
              </p>
            </div>
          </div>

          {/* Caption preview */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-cream/25 text-[10px] uppercase tracking-wider">Caption Preview</p>
              <button
                onClick={() => handleCopy('caption', shareCaption)}
                className="inline-flex items-center gap-1.5 text-[10px] text-cream/30 hover:text-gold transition-colors"
                data-testid="copy-full-caption-btn"
              >
                {copied === 'caption' ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                {copied === 'caption' ? 'Copied!' : 'Copy all'}
              </button>
            </div>
            <pre className="text-cream/40 text-xs whitespace-pre-wrap leading-relaxed font-sans">
              {shareCaption}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuoteCards;
